import { Button } from '@/components/ui/button';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useStore } from '@nanostores/react';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { EventType } from '@/api';  // Make sure this imports from where your enum is defined
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { CalendarDatePicker } from '@/components/calenderDatepicker';
import { useQueryClient } from '@tanstack/react-query';


interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function NewExperimentDrawer({ open, setOpen }: Props) {
  const org = useStore($org);
  const experiments = useStore($experiments);
  const loadingExperiments = useStore($loadingExperiments);
  const queryClient = useQueryClient();
  // If event types aren't loaded yet, trigger the load
  useEffect(() => {
    if (!experiments.length && org?.id) {
      console.log("Loading experiments... again");
      loadExperiments(org.id);
    }
  }, [org?.id]);

  // Create form schema after we have event types
  const formSchema = z.object({
    Name: z.string().min(2, {
      message: 'Score Logic name must be at least 2 characters.',
    }),
    description: z.string().nullable().optional(),
    startDate: z.date(),
    endDate: z.date().nullable().optional(),
    workflowIds: z.array(z.string()),
    accountReceivablesIds: z.array(z.string()),
    variantNames: z.array(z.string()),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      Name: 'Test Experiment',
      description: null,
      startDate: new Date(),
      endDate: null,
      workflowIds: [],
      accountReceivablesIds: [],
      variantNames: [],
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  async function onSubmit(values: z.infer<typeof formSchema>) {
    const result = await createExperiment(
      org?.id || '',
      values.Name || '',
      values.workflowIds,
      values.startDate.toISOString(),
      values.endDate?.toISOString() ?? null,
      values.accountReceivablesIds,
      values.variantNames,  
    );
    if (!result) {
      toast('Error creating new experiment:', {
        duration: 5000,
      });
    } else {
      queryClient.invalidateQueries({ queryKey: ['experiments'] });
      toast.success('Experiment created successfully');
    }

    setIsLoading(false);
    setOpen(false);
  }

  return (
    <Sheet
      open={open}
      onOpenChange={o => {
        form.reset();
        setOpen(o);
      }}
    >
      <SheetContent className="overflow-y-auto sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Create new Experiment</SheetTitle>
          <SheetDescription>
            Fill in the details for the new experiment.
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="mt-4 space-y-8">
            <FormField
              control={form.control}
              name="Name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Experiment Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Experiment ABC" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter an experiment name to help identify this experiment.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input placeholder="Description" {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormDescription>
                    Enter a description for this experiment.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start Date</FormLabel>
                  <br />
                  <FormControl>
                    <CalendarDatePicker field={{ ...field, value: field.value ?? null }} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="endDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>End Date</FormLabel>
                  <br />
                  <FormControl>
                    <CalendarDatePicker field={{ ...field, value: field.value ?? null }} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="workflowIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Workflows</FormLabel>
                  <FormControl>
                    <Input 
                      type="text" 
                      placeholder="Comma-separated workflow IDs" 
                      {...field}
                      onChange={e => field.onChange(e.target.value.split(',').map(id => id.trim()))}
                      value={field.value?.join(', ') ?? ''} 
                    />
                  </FormControl>
                  <FormDescription>
                    Enter workflow IDs separated by commas
                  </FormDescription>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="accountReceivablesIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Receivables</FormLabel>
                  <FormControl>
                    <Input 
                      type="text" 
                      placeholder="Comma-separated account receivable IDs" 
                      {...field}
                      onChange={e => field.onChange(e.target.value.split(',').map(id => id.trim()))}
                      value={field.value?.join(', ') ?? ''} 
                    />
                  </FormControl>
                  <FormDescription>
                    Enter account receivable IDs separated by commas
                  </FormDescription>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="variantNames"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Variant Names</FormLabel>
                  <FormControl>
                  <Input 
                      type="text" 
                      placeholder="Comma-separated variant names" 
                      {...field}
                      onChange={e => field.onChange(e.target.value.split(',').map(id => id.trim()))}
                      value={field.value?.join(', ') ?? ''} 
                    />
                  </FormControl>
                  <FormDescription>
                    Enter variant names separated by commas
                  </FormDescription>
                </FormItem>
              )}
            />
            <SheetFooter>
              <SheetClose asChild>
                <Button variant="outline">Cancel</Button>
              </SheetClose>
              <Button
                type="submit"
                disabled={isLoading}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}